import { BASE_URL } from '@/environment/environment'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { onboardingApiUrl } from '@/modules/onboarding/utils/demoApi'
import type { ICreateImprovementPayload } from '@/modules/improvements/types/ICreateImprovementPayload'
import type { IImprovementFilters } from '@/modules/improvements/types/IImprovementFilters'
import { getEmptyImprovementFilters } from '@/modules/improvements/types/IImprovementFilters'
import type { ICommissionMember, IImprovementsStoreState } from '@/modules/improvements/types/IImprovementsStoreState'
import type { IImprovementSuggestion } from '@/modules/improvements/types/IImprovementSuggestion'
import type {
  ICreateSuggestionTypePayload,
  ISuggestionType,
  IUpdateSuggestionTypePayload,
} from '@/modules/improvements/types/ISuggestionType'
import type { ISuggestionAuthorStat } from '@/modules/improvements/types/ISuggestionAuthorStat'
import type {
  ICommissionCreatePayload,
  ICreateSuggestionPayload,
  IExpertVotePayload,
  IUpdateSuggestionPayload,
  IUpdateTypeCommissionPayload,
} from '@/modules/improvements/types/IPpuPayloads'
import type { IUpdateImprovementPayload } from '@/modules/improvements/types/IUpdateImprovementPayload'
import type { IRealtimePayload } from '@/modules/centrifuge/types/IRealtimePayload'
import { logger } from '@/utils/eventLogger'
import { toApiDate } from '@/utils/formatters'
import { isHttpNotFound } from '@/utils/httpErrors'
import { normalizeAggregatedStatusKey, normalizeStageKey } from '@/modules/improvements/utils/ppuStatusHelper'
import axios from 'axios'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { IImprovementExpert } from '@/modules/improvements/types/IImprovementExpert'
import type { IEmployeeTreeDepartment } from '@/modules/apiStore/types/employees/IEmployeeTree'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'

/*
 * Хранилище предложений по улучшению
 *
 * Основные функции:
 * - Загрузка и фильтрация списка предложений (с пагинацией через cursor)
 * - Создание/редактирование предложений
 * - Голосование (лайк/дизлайк) с мгновенным UX и синхронизацией с сервером
 *
 * Особенности:
 * - Сохраняет локальные голоса пользователя в localStorage
 * - Имеет геттеры для удобного получения количества голосов и проверки возможности голосовать
 * - Синхронизирует быстрые фильтры с параметрами API
 */

let fetchingTypesPromise: Promise<ISuggestionType[]> | null = null
let fetchingAvailableTypesPromise: Promise<ISuggestionType[]> | null = null
let fetchingSuggestionsPromise: Promise<void> | null = null

export const useImprovementsStore = defineStore('improvementsStore', {
  state: (): IImprovementsStoreState => ({
    items: [],
    totalCount: 0,
    likeInProgress: false,
    nextCursor: null as string | null,
    filters: getEmptyImprovementFilters(),
    isLoading: false,
    experts: [] as IImprovementExpert[],
    totalExpertsCount: 0,
    suggestionTypes: [],
    totalSuggestionTypesCount: 0,
    availableSuggestionTypes: [],
    currentSuggestion: null,
    editableSuggestion: null,
    isLoadingDetail: false,
    authorStats: [] as ISuggestionAuthorStat[],
    currentCommission: [] as ICommissionMember[],
    editableCommission: null as ICommissionMember[] | null,
    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},
    currentSortString: null as string | null,
  }),

  getters: {
    hasActiveFilters(state): boolean {
      const f = state.filters
      if (!f) return false
      if (f.search && f.search.trim() !== '') return true
      if (f.mine || f.my_department) return true
      if (f.requires_action !== null && f.requires_action !== undefined) return true
      if (f.created_after !== null || f.created_before !== null) return true
      if (f.decision_after !== null || f.decision_before !== null) return true
      if (Array.isArray(f.status) && f.status.length > 0) return true
      if (Array.isArray(f.stages) && f.stages.length > 0) return true
      if (Array.isArray(f.departments) && f.departments.length > 0) return true
      if (Array.isArray(f.creators) && f.creators.length > 0) return true
      if (Array.isArray(f.coordinators) && f.coordinators.length > 0) return true
      if (Array.isArray(f.suggestion_types) && f.suggestion_types.length > 0) return true
      return false
    },
  },

  actions: {

    /** Загружает статистику авторов ППУ (Рейтинг) и сохраняет в стейт */
    async fetchAuthorStats() {
      this.isLoading = true
      try {
        const response = await axios.get(onboardingApiUrl('improvement', '/improvements/suggestion/author-stats/'))
        const data = response.data?.results ?? response.data ?? []
        this.authorStats = Array.isArray(data) ? data : []
        return this.authorStats
      } catch (error) {
        console.error('fetchAuthorStats_error:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить рейтинг ППУ'),
          time: 3000,
        })
        this.authorStats = []
        return []
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Загружает виды ППУ (настройки).
     * Бэк отдаёт полный список без пагинации (pagination_class = None).
     */
    async fetchSuggestionTypes(): Promise<ISuggestionType[]> {
      if (fetchingTypesPromise)
        return await fetchingTypesPromise

      const runFetch = async () => {
        try {
          const response = await axios.get(onboardingApiUrl('improvement', '/improvements/suggestion-types/'))
          const results: ISuggestionType[] = response.data.results ?? response.data
          const list = Array.isArray(results) ? results : []

          this.suggestionTypes = list
          this.totalSuggestionTypesCount = typeof response.data?.count === 'number' ? response.data.count : this.suggestionTypes.length
          return this.suggestionTypes
        } catch (error) {
          console.error('fetchSuggestionTypes_error:', error)
          const feedbackStore = useFeedbackStore()
          feedbackStore.showToast({
            type: 'error',
            message: getApiError(error, 'Не удалось загрузить виды ППУ'),
            time: 3000,
          })
          return this.suggestionTypes
        }
      }

      fetchingTypesPromise = runFetch().finally(() => {
        fetchingTypesPromise = null
      })
      return await fetchingTypesPromise
    },

    /**
     * Виды для селекта формы: GET ?is_available=true (активные с председателем).
     * Бэк отдаёт полный список без пагинации (pagination_class = None).
     */
    async fetchAvailableSuggestionTypes(): Promise<ISuggestionType[]> {
      if (fetchingAvailableTypesPromise)
        return await fetchingAvailableTypesPromise

      const runFetch = async () => {
        try {
          const response = await axios.get(`${BASE_URL}/api/improvements/suggestion-types/`, {
            params: { is_available: true },
          })
          const results: ISuggestionType[] = response.data.results ?? response.data
          const list = Array.isArray(results) ? results : []

          this.availableSuggestionTypes = list
          return this.availableSuggestionTypes
        } catch (error) {
          console.error('fetchAvailableSuggestionTypes_error:', error)
          const feedbackStore = useFeedbackStore()
          feedbackStore.showToast({
            type: 'error',
            message: getApiError(error, 'Не удалось загрузить виды ППУ'),
            time: 3000,
          })
          return this.availableSuggestionTypes
        }
      }

      fetchingAvailableTypesPromise = runFetch().finally(() => {
        fetchingAvailableTypesPromise = null
      })
      return await fetchingAvailableTypesPromise
    },

    /** Создает новый вид ППУ (POST /api/improvements/suggestion-types/) */
    async createSuggestionType(payload: ICreateSuggestionTypePayload) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(`${BASE_URL}/api/improvements/suggestion-types/`, payload)
        await this.fetchSuggestionTypes()
        await this.fetchAvailableSuggestionTypes()
        feedbackStore.showToast({
          type: 'success',
          message: 'Вид ППУ успешно создан',
          time: 3000,
        })
        return response.data
      } catch (error) {
        console.error('createSuggestionType_error:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать вид ППУ'),
          time: 3000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Обновляет вид ППУ (PATCH /api/improvements/suggestion-types/{id}/) */
    async updateSuggestionType(id: string, payload: IUpdateSuggestionTypePayload) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch(`${BASE_URL}/api/improvements/suggestion-types/${id}/`, payload)
        await this.fetchSuggestionTypes()
        await this.fetchAvailableSuggestionTypes()
        feedbackStore.showToast({
          type: 'success',
          message: 'Вид ППУ сохранен',
          time: 3000,
        })
        return response.data
      } catch (error) {
        console.error('updateSuggestionType_error:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить вид ППУ'),
          time: 3000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Переключает активность вида ППУ (PATCH is_active) */
    async toggleSuggestionTypeActive(id: string, isActive: boolean) {
      const type = this.suggestionTypes.find(t => t.id === id)
      if (!type) return false

      const previous = type.is_active
      type.is_active = isActive

      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.patch(`${BASE_URL}/api/improvements/suggestion-types/${id}/`, { is_active: isActive })
        await this.fetchAvailableSuggestionTypes()
        feedbackStore.showToast({
          type: 'success',
          message: isActive ? 'Вид ППУ активирован' : 'Вид ППУ деактивирован',
          time: 3000,
        })
        return true
      } catch (error) {
        type.is_active = previous
        console.error('toggleSuggestionTypeActive_error:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить активность вида ППУ'),
          time: 3000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Удаляет вид ППУ (DELETE /api/improvements/suggestion-types/{id}/) */
    async deleteSuggestionType(id: string) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.delete(`${BASE_URL}/api/improvements/suggestion-types/${id}/`)
        this.suggestionTypes = this.suggestionTypes.filter(t => String(t.id) !== String(id))
        this.availableSuggestionTypes = this.availableSuggestionTypes.filter(t => String(t.id) !== String(id))
        feedbackStore.showToast({
          type: 'success',
          message: 'Вид ППУ удален',
          time: 3000,
        })
        return true
      } catch (error) {
        console.error('deleteSuggestionType_error:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить вид ППУ'),
          time: 3000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Загружает список экспертов ППУ (GET /api/improvements/experts/) */
    async fetchExperts() {
      this.isLoading = true
      try {
        const response = await axios.get(onboardingApiUrl('improvement', '/improvements/experts/'))
        const expertsList = response.data?.results ?? response.data ?? []
        this.experts = Array.isArray(expertsList) ? expertsList : []
        this.totalExpertsCount = typeof response.data?.count === 'number' ? response.data.count : this.experts.length
        return {
          results: this.experts,
          count: this.totalExpertsCount,
        }
      } catch (error) {
        console.error('fetchExperts_error:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить список экспертов'),
          time: 3000,
        })
        this.experts = []
        this.totalExpertsCount = 0
        return { results: [], count: 0 }
      } finally {
        this.isLoading = false
      }
    },

    /** Загружает дерево сотрудников-экспертов ППУ (GET /api/employee/employees/tree/?is_ppu_expert=true) */
    async fetchPpuExpertsTree(): Promise<IEmployeeTreeDepartment[]> {
      try {
        const response = await axios.get(`${BASE_URL}/api/employee/employees/tree/`, {
          params: { is_ppu_expert: true },
        })
        const resData = response.data?.results ?? response.data ?? []
        return Array.isArray(resData) ? resData : []
      } catch (error) {
        console.error('fetchPpuExpertsTree_error:', error)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить список экспертов'),
          time: 7000,
        })
        return []
      }
    },

    /** Добавляет экспертов ППУ (POST /api/improvements/experts/bulk_create/) */
    async addExperts(employeeIds: string[]) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(`${BASE_URL}/api/improvements/experts/bulk_create/`, {
          employee_ids: employeeIds,
        })
        if (response.status === 200 || response.status === 201) {
          await this.fetchExperts()
          feedbackStore.showToast({
            type: 'success',
            message: 'Эксперты успешно добавлены',
            time: 3000,
          })
          return true
        }
        return false
      } catch (error) {
        console.error('addExperts_error:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось добавить экспертов'),
          time: 3000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Удаляет эксперта ППУ по ID записи (DELETE /api/improvements/experts/{id}/) */
    async deleteExpert(id: number) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.delete(`${BASE_URL}/api/improvements/experts/${id}/`)
        this.experts = this.experts.filter(e => e.id !== id)
        feedbackStore.showToast({
          type: 'success',
          message: 'Эксперт удален',
          time: 3000,
        })
        return true
      } catch (error) {
        console.error('deleteExpert_error:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить эксперта'),
          time: 3000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Устанавливает текущую комиссию в стейт для указанного вида ППУ */
    setCurrentCommissionForType(typeId: string) {
      const typeItem = this.suggestionTypes.find(t => t.id === typeId)
      if (!typeItem) {
        this.currentCommission = []
        this.editableCommission = []
        return
      }

      const list: ICommissionMember[] = []

      if (typeItem.chair) {
        const c = typeItem.chair
        const fullName = [c.last_name, c.first_name, c.middle_name].filter(Boolean).join(' ') || ''
        list.push({
          id: c.id,
          name: fullName,
          position: c.position_name || c.position?.name || '',
          department: c.department_name || c.department?.name || '',
          role: 'Председатель',
          avatarColor: 'primary',
        })
      }

      if (typeItem.deputies && typeItem.deputies.length) {
        typeItem.deputies.forEach(deputy => {
          const fullName = [deputy.last_name, deputy.first_name, deputy.middle_name].filter(Boolean).join(' ') || ''
          list.push({
            id: deputy.id,
            name: fullName,
            position: deputy.position_name || deputy.position?.name || '',
            department: deputy.department_name || deputy.department?.name || '',
            role: 'Эксперт',
            avatarColor: 'secondary',
          })
        })
      }

      this.currentCommission = list
      this.editableCommission = JSON.parse(JSON.stringify(list))
    },

    /** Сохраняет текущую комиссию (currentCommission) вида ППУ в бэкенд */
    async saveCurrentCommission(typeId: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const chairMember = this.currentCommission.find(m => m.role === 'Председатель')
        const deputyMembers = this.currentCommission.filter(m => m.role === 'Эксперт')

        const payload: IUpdateTypeCommissionPayload = {
          chair: chairMember ? { id: chairMember.id } : null,
          deputies: deputyMembers.map(d => ({ id: d.id })),
        }

        const response = await axios.patch(`${BASE_URL}/api/improvements/suggestion-types/${typeId}/`, payload)
        if (response.status === 200 || response.status === 201) {
          const updatedType: ISuggestionType = response.data
          const idx = this.suggestionTypes.findIndex(t => String(t.id) === String(typeId))
          if (idx !== -1) {
            this.suggestionTypes.splice(idx, 1, updatedType)
          }
          const availableIdx = this.availableSuggestionTypes.findIndex(t => String(t.id) === String(typeId))
          if (availableIdx !== -1) {
            this.availableSuggestionTypes.splice(availableIdx, 1, updatedType)
          }
          await this.fetchAvailableSuggestionTypes()
          this.setCurrentCommissionForType(typeId)
          feedbackStore.showToast({
            type: 'success',
            message: 'Состав комиссии успешно сохранен',
            time: 3000,
          })
          return true
        }
        return false
      } catch (error) {
        console.error('saveCurrentCommission_error:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось сохранить состав комиссии'),
          time: 5000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Загружает подробную карточку ППУ */
    async fetchSuggestionById(id: number) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      this.isLoadingDetail = true
      try {
        const response = await axios.get(`${BASE_URL}/api/improvements/suggestion/${id}/`)
        this.currentSuggestion = response.data
        this.editableSuggestion = JSON.parse(JSON.stringify(response.data))
        return response.data
      } catch (error) {
        console.error('fetchSuggestionById_error:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить детальные данные предложения'),
          time: 3000,
        })
        if (isHttpNotFound(error))
          throw error
        return null
      } finally {
        this.isLoadingDetail = false
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает список предложений с учётом фильтров и курсора пагинации
     */
    async fetchSuggestions(cursor?: string, limit?: number, throwOnError = false) {
      if (!cursor && fetchingSuggestionsPromise) {
        if (throwOnError) throw new Error('Suggestions request in progress')
        return fetchingSuggestionsPromise
      }

      const runFetch = async () => {
        const params: Record<string, string | number | boolean> = {}
        const filters = this.filters

        if (limit) {
          filters.page_size = limit
        }

        if (filters.search) params.search = filters.search
        if (filters.status?.length) {
          params.status = filters.status.map(normalizeAggregatedStatusKey).join(',')
        }
        if (filters.stages?.length) {
          params.stages = filters.stages.map(normalizeStageKey).join(',')
        }
        if (filters.departments?.length) params.departments = filters.departments.join(',')
        if (filters.creators?.length) params.creators = filters.creators.join(',')
        if (filters.coordinators?.length) params.coordinators = filters.coordinators.join(',')
        if (filters.suggestion_types?.length) params.suggestion_types = filters.suggestion_types.join(',')

        const createdAfter = toApiDate(filters.created_after)
        if (createdAfter) params.created_after = createdAfter
        const createdBefore = toApiDate(filters.created_before)
        if (createdBefore) params.created_before = createdBefore
        const decisionAfter = toApiDate(filters.decision_after)
        if (decisionAfter) params.decision_after = decisionAfter
        const decisionBefore = toApiDate(filters.decision_before)
        if (decisionBefore) params.decision_before = decisionBefore

        if (filters.mine) params.mine = true
        if (filters.my_department) params.my_department = true
        if (filters.requires_action !== null && filters.requires_action !== undefined) {
          params.requires_action = filters.requires_action
        }
        if (filters.page_size) params.page_size = filters.page_size
        if (cursor) params.cursor = cursor

        this.isLoading = true
        try {
          const res = await axios.get(`${BASE_URL}/api/improvements/suggestion/`, { params })
          const results = res.data.results
          this.items = cursor ? [...this.items, ...results] : results
          this.totalCount = res.data.count ?? (cursor ? this.items.length : results.length)
          this.nextCursor = res.data.next ?? null
        } catch (error) {
          if (throwOnError) throw error
          console.error('fetchSuggestions_error:', error)
          const feedbackStore = useFeedbackStore()
          feedbackStore.showToast({
            type: 'error',
            message: getApiError(error, 'Не удалось загрузить список предложений'),
            time: 3000,
          })
        } finally {
          this.isLoading = false
        }
      }

      if (!cursor) {
        fetchingSuggestionsPromise = runFetch().finally(() => {
          fetchingSuggestionsPromise = null
        })
        return fetchingSuggestionsPromise
      }

      return runFetch()
    },

    resetFilters() {
      const pageSize = this.filters.page_size
      this.filters = {
        ...getEmptyImprovementFilters(),
        page_size: pageSize,
      }
    },

    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string, _module?: IUniversalTreeSelectModule) {
      if (!fieldKey) return
      this.filterLoading[fieldKey] = true

      try {
        const params: Record<string, string | number | boolean> = { field: fieldKey }
        if (search) params.search_field = search
        if (cursor) params.cursor = cursor

        const filters = this.filters as IImprovementFilters

        if (fieldKey !== 'status' && filters.status?.length) {
          params.status = filters.status.join(',')
        }
        if (fieldKey !== 'stages' && filters.stages?.length) {
          params.stages = filters.stages.join(',')
        }
        if (fieldKey !== 'departments' && filters.departments?.length) {
          params.departments = filters.departments.join(',')
        }
        if (fieldKey !== 'creators' && filters.creators?.length) {
          params.creators = filters.creators.join(',')
        }
        if (fieldKey !== 'coordinators' && filters.coordinators?.length) {
          params.coordinators = filters.coordinators.join(',')
        }
        if (fieldKey !== 'suggestion_types' && filters.suggestion_types?.length) {
          params.suggestion_types = filters.suggestion_types.join(',')
        }

        const createdAfter = toApiDate(filters.created_after)
        if (createdAfter) params.created_after = createdAfter
        const createdBefore = toApiDate(filters.created_before)
        if (createdBefore) params.created_before = createdBefore
        const decisionAfter = toApiDate(filters.decision_after)
        if (decisionAfter) params.decision_after = decisionAfter
        const decisionBefore = toApiDate(filters.decision_before)
        if (decisionBefore) params.decision_before = decisionBefore

        if (filters.mine) params.mine = true
        if (filters.my_department) params.my_department = true
        if (filters.search) params.search = filters.search

        const response = await axios.get(`${BASE_URL}/api/improvements/suggestion/filter-choices/`, { params })
        const { results, next, strategy } = response.data

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...results]
            : results
        }
        this.filterCursors[fieldKey] = next
      } catch (e) {
        console.error(`Ошибка загрузки фильтров ППУ [${fieldKey}]:`, e)
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(e, 'Не удалось загрузить фильтры'),
          time: 7000,
        })
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    /** Создаёт предложение по улучшению и обновляет список */
    async createSuggestion(payload: ICreateImprovementPayload) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(`${BASE_URL}/api/improvements/suggestion/`, payload)
        if (response.status === 201 || response.status === 200) {
          feedbackStore.showToast({
            type: 'success',
            message: 'Предложение успешно создано',
            time: 5000,
          })
          await this.fetchSuggestions()
          return response.data
        }
        return null
      } catch (error) {
        logger.error('createSuggestion_error', {
          store: 'improvementsStore',
          function: 'createSuggestion',
          error: `❌ Ошибка при создании предложения по улучшению: ${error}`,
          payload,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать предложение по улучшению'),
          time: 7000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Обновляет предложение и затем автоматически дергает resubmit */
    async updateSuggestion(id: number, payload: IUpdateSuggestionPayload) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch(`${BASE_URL}/api/improvements/suggestion/${id}/`, payload)
        if (response.status === 200) {
          let updatedData = response.data

          // После успешного патча автоматически дергаем resubmit, если предложение на доработке
          if (response.data?.actions?.includes('resubmit')) {
            try {
              const resubmitRes = await axios.post(`${BASE_URL}/api/improvements/suggestion/${id}/resubmit/`)
              if (resubmitRes.status === 200 || resubmitRes.status === 201) {
                updatedData = resubmitRes.data
              }
            } catch (resubmitError) {
              logger.error('resubmitSuggestion_auto_error', {
                store: 'improvementsStore',
                function: 'updateSuggestion',
                error: `❌ Ошибка авто-resubmit: ${resubmitError}`,
                payload: `${id}`,
              })
              feedbackStore.showToast({
                type: 'error',
                message: getApiError(resubmitError, 'Не удалось отправить предложение на рассмотрение'),
                time: 7000,
              })
              this.currentSuggestion = updatedData
              this.editableSuggestion = JSON.parse(JSON.stringify(updatedData))
              const failedIdx = this.items.findIndex(i => i.id === id)
              if (failedIdx !== -1) {
                this.items.splice(failedIdx, 1, updatedData)
              }
              return updatedData
            }
          }

          this.currentSuggestion = updatedData
          this.editableSuggestion = JSON.parse(JSON.stringify(updatedData))
          const idx = this.items.findIndex(i => i.id === id)
          if (idx !== -1) {
            this.items.splice(idx, 1, updatedData)
          }
          feedbackStore.showToast({
            type: 'success',
            message: 'Предложение обновлено и отправлено на рассмотрение',
            time: 5000,
          })
          return updatedData
        }
      } catch (error) {
        logger.error('updateSuggestion_error', {
          store: 'improvementsStore',
          function: 'updateSuggestion',
          error: `❌ Ошибка обновления предложения по улучшению: ${error}`,
          payload,
        })
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить предложение по улучшению'),
          time: 7000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async likeSuggestion(id: number) {
      this.likeInProgress = true
      try {
        await axios.post(`${BASE_URL}/api/improvements/suggestion/${id}/like/`)
        await this.fetchSuggestions()
      } catch (error) {
        logger.error('likeSuggestion_error', {
          store: 'improvementsStore',
          function: 'likeSuggestion',
          error: `❌ Не удалось лайкнуть предложение: ${error}`,
          payload: `${id}`,
        })
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось поставить отметку «Мне нравится»'),
          time: 3000,
        })
      } finally {
        this.likeInProgress = false
      }
    },

    async dislikeSuggestion(id: number) {
      this.likeInProgress = true
      try {
        await axios.post(`${BASE_URL}/api/improvements/suggestion/${id}/dislike/`)
        await this.fetchSuggestions()
      } catch (error) {
        logger.error('dislikeSuggestion_error', {
          store: 'improvementsStore',
          function: 'dislikeSuggestion',
          error: `❌ Не удалось дизлайкнуть предложение: ${error}`,
          payload: `${id}`,
        })
        const feedbackStore = useFeedbackStore()
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось поставить отметку «Не нравится»'),
          time: 3000,
        })
      } finally {
        this.likeInProgress = false
      }
    },

    /** Загружает файлы к карточке ППУ */
    async uploadSuggestionFiles(id: number, files: File[]) {
      if (!files.length) return true
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const formData = new FormData()
        files.forEach(file => formData.append('files', file))
        const res = await axios.post(`${BASE_URL}/api/improvements/suggestion/${id}/files/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        if (res.data) {
          this.currentSuggestion = res.data
          this.editableSuggestion = JSON.parse(JSON.stringify(res.data))
        }
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось прикрепить файлы'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Удаляет файл с карточки ППУ */
    async deleteSuggestionFile(suggestionId: number, fileId: number) {
      const feedbackStore = useFeedbackStore()
      try {
        await axios.delete(`${BASE_URL}/api/improvements/suggestion/${suggestionId}/files/${fileId}/`)
        await this.fetchSuggestionById(suggestionId)
        return true
      } catch (error) {
        console.error('deleteSuggestionFile_error:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить прикрепленный файл'),
          time: 3000,
        })
        return false
      }
    },

    /** Направить в комиссию (руководитель отделения) */
    async forwardToCommission(id: number) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(`${BASE_URL}/api/improvements/suggestion/${id}/forward-to-commission/`)
        this.currentSuggestion = response.data
        feedbackStore.showToast({
          type: 'success',
          message: 'ППУ направлено в комиссию',
          time: 5000,
        })
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось направить в комиссию'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Вернуть автору на доработку */
    async returnToAuthor(id: number, comment: string) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(`${BASE_URL}/api/improvements/suggestion/${id}/return-to-author/`, {
          comment,
        })
        this.currentSuggestion = response.data
        feedbackStore.showToast({
          type: 'success',
          message: 'ППУ возвращено автору на доработку',
          time: 5000,
        })
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось вернуть автору'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Вернуть руководителю отделения */
    async returnToDepartmentHead(id: number, comment: string) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(`${BASE_URL}/api/improvements/suggestion/${id}/return-to-department-head/`, {
          comment,
        })
        this.currentSuggestion = response.data
        feedbackStore.showToast({
          type: 'success',
          message: 'ППУ возвращено руководителю отделения',
          time: 5000,
        })
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось вернуть руководителю отделения'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Отклонить ППУ */
    async rejectSuggestion(id: number, comment: string) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(`${BASE_URL}/api/improvements/suggestion/${id}/reject/`, {
          comment,
        })
        this.currentSuggestion = response.data
        feedbackStore.showToast({
          type: 'success',
          message: 'ППУ отклонено',
          time: 5000,
        })
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось отклонить ППУ'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Принять ППУ */
    async acceptSuggestion(id: number, comment: string) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post(`${BASE_URL}/api/improvements/suggestion/${id}/accept/`, {
          comment,
        })
        this.currentSuggestion = response.data
        feedbackStore.showToast({
          type: 'success',
          message: 'ППУ принято',
          time: 5000,
        })
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось принять ППУ'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Назначить / изменить экспертов комиссии (PATCH /api/improvements/suggestion/{id}/commission/) */
    async createCommission(id: number, payload: ICommissionCreatePayload) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch(`${BASE_URL}/api/improvements/suggestion/${id}/commission/`, payload)
        this.currentSuggestion = response.data
        feedbackStore.showToast({
          type: 'success',
          message: 'Эксперты комиссии успешно обновлены',
          time: 5000,
        })
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось назначить экспертов'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Удалить эксперта из комиссии (DELETE /api/improvements/suggestion/{id}/commission/{entryId}/) */
    /**
     * Назначение / смена координатора ППУ.
     * Бэк создаёт задачу координатора, если её ещё нет, и возвращает её — кладём в coordinator_entry
     * текущей и эталонной ППУ, чтобы проверка несохранённых изменений не считала смену грязью.
     */
    async changeSuggestionCoordinator(payload: { suggestion_id: number, coordinator_id: string }) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch(
          `${BASE_URL}/api/improvements/suggestion/${payload.suggestion_id}/responsibility-entries/change-coordinator/`,
          { coordinator_id: payload.coordinator_id },
        )

        const updatedCoordinatorEntry = response.data

        if (Number(this.currentSuggestion?.id) === payload.suggestion_id)
          this.currentSuggestion!.coordinator_entry = updatedCoordinatorEntry
        if (Number(this.editableSuggestion?.id) === payload.suggestion_id)
          this.editableSuggestion!.coordinator_entry = updatedCoordinatorEntry

        feedbackStore.showToast({
          type: 'success',
          message: 'Координатор успешно изменён',
          time: 7000,
        })
        return updatedCoordinatorEntry
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось изменить координатора'),
          time: 7000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async removeExpertEntry(suggestionId: number, entryId: number) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.delete(
          `${BASE_URL}/api/improvements/suggestion/${suggestionId}/commission/${entryId}/`,
        )
        if (response.data) {
          this.currentSuggestion = response.data
        }
        feedbackStore.showToast({
          type: 'success',
          message: 'Эксперт удален из комиссии',
          time: 5000,
        })
        await this.fetchSuggestionById(suggestionId)
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить эксперта'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /** Голосование эксперта */
    async expertVote(suggestionId: number, entryId: number, agreementResult: 'confirmed' | 'revision' | 'rejected', report?: string) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.post(
          `${BASE_URL}/api/improvements/suggestion/${suggestionId}/responsibility-entries/${entryId}/agree/`,
          {
            agreement_result: agreementResult,
            report: report || '',
          },
        )
        feedbackStore.showToast({
          type: 'success',
          message: 'Ваш голос принят',
          time: 5000,
        })
        await this.fetchSuggestionById(suggestionId)
        return true
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось отправить голос'),
          time: 7000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * improvement_suggestion_created / updated / deleted.
     * Из сообщения берётся только id: в списочном payload нет latest_review_comment
     * (сериализатор без request.user). Строка и открытая форма пишутся из GET карточки.
     */
    async applyImprovementRealtime(payload: IRealtimePayload): Promise<void> {
      const feedbackStore = useFeedbackStore()
      if (!payload?.event_type || !payload?.data) return

      const id = Number((payload.data as { id?: number }).id)
      if (!Number.isFinite(id) || id <= 0) return

      if (this.hasActiveFilters) {
        await this.fetchSuggestions(undefined, undefined, false)
        if (this.currentSuggestion?.id === id) {
          try {
            const response = await axios.get<IImprovementSuggestion>(`${BASE_URL}/api/improvements/suggestion/${id}/`)
            this.currentSuggestion = response.data
            this.editableSuggestion = JSON.parse(JSON.stringify(response.data))
          } catch (e) {
            console.error('applyImprovementRealtime_error:', e)
          }
        }
        return
      }

      if (payload.event_type === 'improvement_suggestion_deleted') {
        const idx = this.items.findIndex(item => item.id === id)
        if (idx >= 0) {
          const title = this.items[idx]?.title || ''
          this.items.splice(idx, 1)
          if (this.totalCount > 0) this.totalCount--
          feedbackStore.showToast({
            type: 'warning',
            message: `Предложение по улучшению «${title}» удалено`,
            time: 5000,
          })
        }
        return
      }

      if (
        payload.event_type !== 'improvement_suggestion_created'
        && payload.event_type !== 'improvement_suggestion_updated'
      ) return

      try {
        const response = await axios.get<IImprovementSuggestion>(`${BASE_URL}/api/improvements/suggestion/${id}/`)
        const suggestion = response.data
        const idx = this.items.findIndex(item => item.id === suggestion.id)
        if (idx >= 0) {
          this.items.splice(idx, 1, suggestion)
        } else {
          this.items = [suggestion, ...this.items]
          this.totalCount++
        }

        if (this.currentSuggestion?.id === suggestion.id) {
          this.currentSuggestion = suggestion
          this.editableSuggestion = JSON.parse(JSON.stringify(suggestion))
        }

        if (payload.event_type === 'improvement_suggestion_created') {
          feedbackStore.showToast({
            type: 'info',
            message: `Получено новое предложение по улучшению: «${suggestion.title || ''}»`,
            time: 5000,
          })
        }
      } catch (error) {
        console.error('applyImprovementRealtime_error:', error)
      }
    },

    /**
     * Обработка realtime-событий записей ответственности (responsibility_entry_*) для открытого предложения.
     * Payload Centrifuge не пишем: в нём actions пустой. Карточку заново берём с GET.
     */
    async applyResponsibilityEntryRealtime(payload: IRealtimePayload | any): Promise<void> {
      if (!payload?.event_type || !payload?.data) return
      const entry = payload.data as any
      if (entry.source_type !== 'improvementsuggestion') return

      const suggestionId = Number(entry.url_props?.suggestionId)
      if (!suggestionId || this.currentSuggestion?.id !== suggestionId) return

      await this.fetchSuggestionById(suggestionId)
    },

    updateRequiresAction(id: number, requiresAction: boolean) {
      const item = this.items.find(s => Number(s.id) === id)
      if (item) {
        item.requires_action = requiresAction
      }
      if (this.currentSuggestion && Number(this.currentSuggestion.id) === id) {
        this.currentSuggestion.requires_action = requiresAction
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useImprovementsStore, import.meta.hot))
}
